import { Injectable } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { NotificationsService } from './notifications.service';

export type MatchType = 'Men' | 'Women' | 'Mixed' | 'All';
export type ActivelyLinkStatus = 'idle' | 'searching' | 'matched';

export interface ActivelyLinkState {
  status: ActivelyLinkStatus;
  reservationId?: number;
}

export interface SearchingCountResult {
  searchingCount: number;
  userLevel: string;
}

export interface EarliestSlotResult {
  earliestSlot: {
    startTime: string;
    endTime: string;
    courtName: string;
  } | null;
}

@Injectable({ providedIn: 'root' })
export class ActivelyLinkService {
  constructor(
    private supabase: SupabaseService,
    private notificationsService: NotificationsService
  ) {}

  async register(
    matchType: MatchType,
    favoritesOnly: boolean = false,
    facilityId: string | null = null,
    maxRetries = 2
  ): Promise<ActivelyLinkState> {
    const clubId = this.supabase.clubId;

    if (!clubId) {
      throw new Error('Club not found');
    }

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const { data, error } = await this.supabase.supabase.functions.invoke(
        'matchmaking/api/register',
        {
          body: { clubId, matchType, favoritesOnly, facilityId },
        }
      );

      if (error) {
        const msg = error.message || '';
        const isRaceCondition =
          msg.includes('23502') ||
          msg.includes('already exists') ||
          msg.includes('violates not-null');

        if (isRaceCondition && attempt < maxRetries) {
          lastError = new Error(msg);
          await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
          continue;
        }

        throw new Error(msg);
      }

      if (data.status === 'matched') {
        void this.notificationsService.fetchUnreadCount();
      }

      return data as ActivelyLinkState;
    }

    throw lastError ?? new Error('Matchmaking failed after retries');
  }

  async cancel(reservationId: number): Promise<void> {
    const { data, error } = await this.supabase.supabase.functions.invoke(
      'matchmaking/api/cancel',
      {
        body: { reservationId },
      }
    );

    if (error) throw new Error(error.message);
    if (data.error) throw new Error(data.error);
  }

  async unregister(): Promise<void> {
    const clubId = this.supabase.clubId;

    if (!clubId) throw new Error('Club not found');

    const { data, error } = await this.supabase.supabase.functions.invoke(
      'matchmaking/api/unregister',
      {
        body: { clubId },
      }
    );

    if (error) throw new Error(error.message);
    if (data.error) throw new Error(data.error);
  }

  async getStatus(): Promise<ActivelyLinkState> {
    const clubId = this.supabase.clubId;

    if (!clubId) {
      return { status: 'idle' };
    }

    const { data, error } = await this.supabase.supabase.functions.invoke(
      'matchmaking/api/status',
      {
        body: { clubId },
      }
    );

    if (error) {
      console.error('Could not fetch matchmaking status:', error);
      return { status: 'idle' };
    }

    return data as ActivelyLinkState;
  }

  async getSearchingCount(): Promise<SearchingCountResult> {
    const clubId = this.supabase.clubId;

    if (!clubId) {
      return { searchingCount: 0, userLevel: '' };
    }

    const { data, error } = await this.supabase.supabase.functions.invoke(
      'matchmaking/api/searching-count',
      {
        body: { clubId },
      }
    );

    if (error) {
      console.error('Could not fetch searching count:', error);
      return { searchingCount: 0, userLevel: '' };
    }

    return {
      searchingCount: data?.searchingCount ?? 0,
      userLevel: data?.userLevel ?? '',
    };
  }

  async getEarliestSlot(): Promise<EarliestSlotResult> {
    const clubId = this.supabase.clubId;

    if (!clubId) {
      return { earliestSlot: null };
    }

    const { data, error } = await this.supabase.supabase.functions.invoke(
      'matchmaking/api/earliest-slot',
      {
        body: { clubId },
      }
    );

    if (error) {
      console.error('Could not fetch earliest slot:', error);
      return { earliestSlot: null };
    }

    return data as EarliestSlotResult;
  }
}
